import { Component, ErrorInfo, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import RetailMainButton from "components/Button/RetailMainButton";
import RetailPageContainer from "components/Layout/RetailPageContainer";
import RetailTitle from "components/Typography/RetailTitle";
import { errorTracking } from "utils/errorTracking";

interface ErrorBoundaryProps {
  children: ReactNode;
  componentName?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

const extractComponentName = (componentStack: string): string => {
  if (!componentStack) return "Unknown";

  const lines = componentStack.split("\n");
  if (lines.length > 0) {
    const firstLine = lines[0].trim();
    const match = firstLine.match(/in ([A-Za-z0-9_$]+)/);
    if (match && match[1]) return match[1];
  }
  return "Unknown";
};

const extractFilePath = (componentStack: string): string => {
  if (!componentStack) return "Unknown";

  const lines = componentStack.split("\n");
  if (lines.length > 0) {
    const firstLine = lines[0].trim();
    const match = firstLine.match(/\(at ([^:]+)(:\d+:\d+)?\)/);
    if (match && match[1]) return match[1];
  }
  return "Unknown";
};

const ErrorFallback = () => {
  const { t } = useTranslation();

  const handleRefresh = () => window.location.reload();

  return (
    <RetailPageContainer>
      <section
        className="flex"
        style={{
          flexDirection: "column",
          height: "100%",
          justifyContent: "center",
        }}
      >
        <RetailTitle level={5}>{t("common.hasError")}</RetailTitle>
        <RetailMainButton hasBackground onClick={handleRefresh}>
          {t("common.refresh")}
        </RetailMainButton>
      </section>
    </RetailPageContainer>
  );
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { componentName: providedComponentName } = this.props;

    const errorComponentName = extractComponentName(
      errorInfo.componentStack || ""
    );
    const errorFilePath = extractFilePath(errorInfo.componentStack || "");

    const componentName = providedComponentName || errorComponentName;

    errorTracking(error, {
      componentName,
      additionalData: {
        errorComponent: errorComponentName,
        filePath: errorFilePath,
        componentStack:
          errorInfo.componentStack || "No component stack available",
        errorInfo: JSON.stringify(errorInfo),
        errorMessage: error.message,
        errorName: error.name,
        wrapperComponentName: providedComponentName || "Not provided",
        renderedBy: "ErrorBoundary",
      },
    });

    console.error("Error caught by ErrorBoundary:", {
      error,
      errorInfo,
      component: componentName,
      errorComponent: errorComponentName,
      filePath: errorFilePath,
      location: window.location.href,
      timestamp: new Date().toISOString(),
    });

    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) return <ErrorFallback />;

    return this.props.children;
  }
}

export default ErrorBoundary;
